import type { PermissionDataType as PermissionType } from "@clovis/server/src/app/domain/utils/permissions/permissions";
import {
  getOrgData,
  getProjectData,
} from "@clovis/server/src/app/domain/utils/permissions/permissions";
import { CheckIcon } from "@heroicons/react/solid";
import { sortBy } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import type { CellProps, Column } from "react-table";
import { i18n } from "~/config/i18next";
import type { CSS } from "~/config/stitches";
import { getSectionPriority } from "~/config/users-rights-permissions/utils";
import { Badge } from "~/design-system/Badge";
import { Box } from "~/design-system/Box";
import { Icon } from "~/design-system/Icon";
import { CELL_HEIGHT } from "~/design-system/Table";
import { VirtualTable } from "~/screens/App/components/VirtualTable";
import type { LanguageType } from "~/screens/App/screens/AnonRightsScreen/components/LanguageDropdown/LanguageDropdown";
import { PermissionTranslation } from "~/screens/App/screens/AnonRightsScreen/components/PermissionTable/components/PermissionTranslation";
import {
  sectionBadgesColors,
  SectionTranslation,
} from "~/screens/App/screens/AnonRightsScreen/components/PermissionTable/components/SectionTranslation";
import { createComponentHook } from "~/types";

type ProjectPermissionTableProps = {
  type: "org" | "project";
  css?: CSS;
  language: LanguageType;
  filteredPermissions?: PermissionType[];
};

const useProjectPermissionTable = createComponentHook(
  (props: ProjectPermissionTableProps) => {
    const { t } = useTranslation();

    React.useEffect(() => {
      if (props.language !== i18n.language) {
        void i18n.changeLanguage(props.language);
      }
    }, [props.language]);

    // TODO add types on permissions
    // Example of one permission in the array
    //   {
    //     "administrator": true,
    //     "disabled": false,
    //     "limited": false,
    //     "owner": true,
    //     "permission": "UpdateProjectUserInfos",
    //     "readOnly": false,
    //     "section": "user",
    //     "standard": true
    // }
    let permissions = [];
    if (props.filteredPermissions) {
      permissions = props.filteredPermissions;
    } else if (props.type === "org") {
      permissions.push(...getOrgData());
    } else if (props.type === "project") {
      permissions.push(...getProjectData());
    }

    const sortedPermissions = sortBy(permissions, (p) =>
      getSectionPriority(p.section)
    );

    const columns = React.useMemo<Array<Column<PermissionType>>>(
      () => [
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-section ${props.cell.row.original.permission}}`}
              // Here the id is usefull to help to scroll directly to one permission
              id={props.cell.row.original.permission}
            >
              <Badge
                color={sectionBadgesColors[props.cell.row.original.section]}
              >
                <SectionTranslation section={props.cell.row.original.section} />
              </Badge>
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.section", "Section"),
          id: "section",
          width: "150px",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              css={{ whiteSpace: "normal" }}
              data-clovis-action-id={`column-permission ${props.cell.row.original.permission}}`}
            >
              <PermissionTranslation
                permission={props.cell.row.original.permission}
              />
            </Box>
          ),
          Header: () =>
            t(
              "screens.ProjectPermissionTable.columns.permission",
              "Permission"
            ),
          id: "permission",
          width: "450px",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-owner ${props.cell.row.original.permission}}`}
              justifyContent={"center"}
            >
              {props.cell.row.original.owner && (
                <Icon
                  label={`icon-permission ${props.cell.row.original.permission}}`}
                  size="small"
                >
                  <CheckIcon />
                </Icon>
              )}
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.owner", "Owner"),
          id: "owner",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-administrator ${props.cell.row.original.permission}}`}
            >
              {props.cell.row.original.administrator && (
                <Icon
                  label={`icon-permission ${props.cell.row.original.permission}}`}
                  size="small"
                >
                  <CheckIcon />
                </Icon>
              )}
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.admin", "Admin"),
          id: "administrator",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-standard ${props.cell.row.original.permission}}`}
            >
              {props.cell.row.original.standard && (
                <Icon
                  label={`icon-permission ${props.cell.row.original.permission}}`}
                  size="small"
                >
                  <CheckIcon />
                </Icon>
              )}
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.standard", "Standard"),
          id: "standard",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-limited ${props.cell.row.original.permission}}`}
            >
              {props.cell.row.original.limited && (
                <Icon
                  label={`icon-permission ${props.cell.row.original.permission}}`}
                  size="small"
                >
                  <CheckIcon />
                </Icon>
              )}
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.limited", "Limited"),
          id: "limited",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-readOnly ${props.cell.row.original.permission}}`}
            >
              {props.cell.row.original.readOnly && (
                <Icon
                  label={`icon-permission ${props.cell.row.original.permission}}`}
                  size="small"
                >
                  <CheckIcon />
                </Icon>
              )}
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.readOnly", "Read only"),
          id: "readOnly",
        },
        {
          Cell: (props: CellProps<PermissionType>) => (
            <Box
              data-clovis-action-id={`column-disabled ${props.cell.row.original.permission}}`}
            >
              {props.cell.row.original.disabled && (
                <Icon
                  label={`icon-permission ${props.cell.row.original.permission}}`}
                  size="small"
                >
                  <CheckIcon />
                </Icon>
              )}
            </Box>
          ),
          Header: () =>
            t("screens.ProjectPermissionTable.columns.disabled", "disabled"),
          id: "disabled",
        },
      ],
      [t]
    );
    return {
      state: {
        columns,
        sortedPermissions,
      },
    };
  }
);

const TableHeader = ({
  columns,
}: {
  columns: Array<Column<PermissionType>>;
}) => {
  return (
    <Box
      css={{
        backgroundColor: "$gray50",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
      }}
    >
      <VirtualTable
        estimateSize={() => CELL_HEIGHT}
        data={[]}
        columns={columns}
        noHeader={false}
      />
    </Box>
  );
};

const TableBody = ({
  columns,
  data,
}: {
  data: PermissionType[];
  columns: Array<Column<PermissionType>>;
}) => {
  return (
    <Box
      css={{
        height: "63vh",
        overflow: "auto",
      }}
    >
      <VirtualTable
        estimateSize={() => CELL_HEIGHT}
        data={data}
        columns={columns}
        manualSort
        noHeader={true}
      />
    </Box>
  );
};

const PermissionTableDesktop = (props: ProjectPermissionTableProps) => {
  const { state } = useProjectPermissionTable(props);
  return (
    <Box css={props.css}>
      <TableHeader columns={state.columns} />
      <TableBody data={state.sortedPermissions} columns={state.columns} />
    </Box>
  );
};

export { PermissionTableDesktop };
